import reportError from 'airship/modules/report-error'
import getDefaultSdk from 'airship/utils/web-sdk'

import * as debugLog from '../debug-log'
import {AnalyticsBackend, getBackend} from './backend'
import {
  AirshipSdkClient,
  AMPLITUDE_LABEL,
  AmplitudeClientImpl,
  AppcuesClient,
  GenericClient,
  SegmentClient,
} from './clients'
import {AnalyticsDisabledError} from './errors'
import {
  Action,
  AnalyticsProvider,
  Context,
  EventBackendProps,
  EventProps,
  EventPropsSchema,
  InjectedWindow,
  LegacyEventProps,
  UserBackendProps,
  UserNumericPropName,
} from './types'
import {joinEventName} from './util'

const injectedWindow = window as InjectedWindow

// https://help.amplitude.com/hc/en-us/articles/115002889587-JavaScript-SDK-Reference
const Amplitude = new AmplitudeClientImpl(
  getBackend(),
  () => injectedWindow.amplitude?.getInstance(),
  () => injectedWindow.amplitude && new injectedWindow.amplitude.Identify()
)
const Airship = new AirshipSdkClient(getDefaultSdk())
const Appcues = new AppcuesClient(injectedWindow.Appcues)
// https://docs.heap.io/reference
const Heap = new GenericClient('Heap', injectedWindow.heap)
// https://developer.mixpanel.com/docs/javascript
const Mixpanel = new GenericClient('Mixpanel', injectedWindow.mixpanel)
// https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/
const Segment = new SegmentClient(injectedWindow.analytics)

const PROVIDERS: AnalyticsProvider[] = [
  Amplitude,
  Heap,
  Mixpanel,
  Segment,
  Appcues,
  Airship,
]

if (PROVIDERS.includes(Airship) && PROVIDERS.includes(Appcues)) {
  Appcues.setupFlowTracking(Airship)
}

/**
 * ProviderPromise is a generic way to track an attempt to do something in an
 * analytics platform. It's purely so we can log the result in a consistent way
 * afterward.
 */
interface ProviderPromise {
  provider: AnalyticsProvider
  promise: Promise<boolean>
}

/**
 * Provides a generic API for sending to an arbitrary number of analytics
 * platforms. Functions are heavily guarded to prevent errors from interrupting
 * customer logic.
 */
export class Analytics {
  backend: AnalyticsBackend
  providers: AnalyticsProvider[]

  public constructor(
    backend: AnalyticsBackend,
    providers: AnalyticsProvider[] = PROVIDERS
  ) {
    this.backend = backend
    this.providers = providers
  }

  /**
   * Increment or decrement an integer property.
   *
   * NOTE: For expediency, this currently works only with Amplitude. If we end up
   * with some other analytics provider in the future, we'll need to update this
   * if possible.
   */
  public async incrementProp(
    name: UserNumericPropName,
    value = 1
  ): Promise<void> {
    const combinedProps = await this.getCombinedEventProps({})
    if (combinedProps.userProps) {
      this.setUserProps(combinedProps.userProps).catch((err) => {
        reportError(err)
      })
    }

    const amplitudeClient = this.providers.filter(
      (provider) => provider.label === AMPLITUDE_LABEL
    )

    try {
      amplitudeClient.forEach(async (amplitude) => {
        const result = await (amplitude as AmplitudeClientImpl).add(
          name,
          value,
          combinedProps.eventProps
        )
        if (result) {
          debugLog.log('Incremented analytics property "%s" by %s', name, value)
        }
      })
    } catch (e) {
      reportError(e)
    }
  }

  /**
   * Combines event properties with looked up backend event properties and
   * returns the result.
   * @param props Event properties to set
   */
  private async getCombinedEventProps(props: EventProps): Promise<{
    eventProps: EventProps & EventBackendProps
    userProps: UserBackendProps | undefined
  }> {
    let eventProps: EventProps & EventBackendProps = props
    let userProps: UserBackendProps | undefined
    try {
      const backendProps = await this.backend.getEventProperties()
      if (backendProps) {
        userProps = backendProps.user

        eventProps = {...eventProps, ...backendProps.request}
        if (backendProps.event) {
          eventProps = {...eventProps, ...backendProps.event}

          if (backendProps.event.appKey) {
            userProps.mostRecentAppKey = backendProps.event.appKey
          }
        }
      }
    } catch (err) {
      if (err instanceof AnalyticsDisabledError) {
        // if analytics are disabled, let the error propagate
        throw err
      }
      reportError(err)
    }
    return {
      eventProps,
      userProps,
    }
  }

  /**
   * tracks is for _structured_ tracking. It's the new method for reporting to
   * new platforms, and will help force enforce our event taxonomy.
   *
   * This will report events with names like "Composer: Click Message Type".
   */
  public async tracks(
    context: Context,
    action: Action,
    description: string,
    data?: EventPropsSchema
  ): Promise<void> {
    try {
      await this.sendTrackEvent(context, action, description, data)
    } catch (err) {
      if (err instanceof AnalyticsDisabledError) {
        // if analytics are disabled, don't report
        debugLog.log('Analytics are disabled.')
        return
      }
      reportError(err)
    }
  }

  /**
   * DEPRECATED. track is the legacy method to report to Kissmetrics. Events
   * will be sent to other platforms but will tagged with a legacy prefix.
   *
   * This is still around for compatibility with old code.
   * @deprecated
   */
  public async track(
    eventName: string,
    data?: LegacyEventProps
  ): Promise<void> {
    try {
      // Send to modern (non-Kissmetrics) platorms with the legacy prefix.
      await this.sendLegacyTrackEvent(`[Legacy] ${eventName}`, data)
    } catch (err) {
      if (err instanceof AnalyticsDisabledError) {
        // if analytics are disabled, don't report
        debugLog.log('Analytics are disabled.')
        return
      }
      reportError(err)
    }
  }

  /**
   * Set user-level properties. We set these behind the scenes based on
   * properties that the backend provides. These can be changed by the frontend.
   */
  public async setUserProps(props: UserBackendProps): Promise<void> {
    const promises: ProviderPromise[] = this.providers.map((provider) => {
      return {
        provider,
        // Send a copy of the object in case the client modifies it.
        promise: provider.setUserProps({...props}),
      }
    })
    const sentTo = await this.getSuccessProviderNames(promises)
    debugLog.log(
      'Set user properties in %s:',
      sentTo.length ? sentTo.join(', ') : 'no platforms',
      props
    )
  }

  /**
   * Send a tracking event via the given providers.
   */
  private async sendTrackEvent(
    context: Context,
    action: Action,
    description: string,
    props?: EventProps
  ): Promise<void> {
    if (!props) {
      props = {}
    }

    const combinedProps = await this.getCombinedEventProps(props)

    if (combinedProps.userProps) {
      this.setUserProps(combinedProps.userProps).catch((err) => {
        reportError(err)
      })
    }

    const promises: ProviderPromise[] = this.providers.map((provider) => {
      return {
        provider,
        // Send a copy of the object in case the client modifies it.
        promise: provider.track(context, action, description.trim(), {
          ...combinedProps.eventProps,
        }),
      }
    })
    const sentTo = await this.getSuccessProviderNames(promises)
    debugLog.log(
      'Sent analytics event "%s" to %s',
      joinEventName(context, action, description),
      sentTo.length ? sentTo.join(', ') : 'no platforms',
      combinedProps
    )
  }

  /**
   * Send a legacy tracking event via the given providers.
   */
  private async sendLegacyTrackEvent(
    eventName: string,
    props?: EventProps
  ): Promise<void> {
    eventName = eventName.trim()
    if (!props) {
      props = {}
    }

    const combinedProps = await this.getCombinedEventProps(props)

    if (combinedProps.userProps) {
      this.setUserProps(combinedProps.userProps).catch((err) => {
        reportError(err)
      })
    }

    const promises: ProviderPromise[] = this.providers
      .filter((p) => p.supportsLegacyEvents)
      .map((provider) => {
        return {
          provider,
          // Send a copy of the object in case the client modifies it.
          promise: provider.trackLegacyEvent(eventName, {
            ...combinedProps.eventProps,
          }),
        }
      })
    const sentTo = await this.getSuccessProviderNames(promises)
    debugLog.log(
      'Sent analytics event "%s" to %s',
      eventName,
      sentTo.length ? sentTo.join(', ') : 'no platforms',
      combinedProps
    )
  }

  /**
   * Return an array of names of the providers we successfully sent to.
   */
  private async getSuccessProviderNames(
    promises: ProviderPromise[]
  ): Promise<string[]> {
    const result: string[] = []
    for (const p of promises) {
      try {
        const success = await p.promise
        if (success) {
          result.push(p.provider.label)
        }
      } catch (err) {
        reportError(err)
      }
    }
    return result
  }

  /**
   * Flushes all batched events.
   *
   * NOTE: For expediency, this currently works only with Amplitude. If we end up
   * with some other analytics provider in the future, we'll need to update this
   * if possible.
   */
  public async flushEvents(): Promise<boolean> {
    return await Amplitude.flushEvents()
  }
}

export function getAnalytics(): Analytics {
  return new Analytics(getBackend())
}

/**
 * Track an event in our analytics platform (currently Amplitude).
 *
 * **IMPORTANT: Avoid templating any unbound values into the `description` field.
 * Amplitude events are identified by concatenating the first 3 arguments together
 * so dynamic content bloats our events and makes them difficult to group and filter.
 * Add any dynamic values (example: Segment ID) as properties on the `data` object.**
 *
 * @example
 * ```
 * //Track an event called "Composer: Click Send" and include the ComposerData ID
 * // DO this:
 * track('Composer', 'Click', 'Send', {composerDataId: 'ABC12345'})
 *
 * // DO NOT do this:
 * track('Composer', 'Click', `Send ${composerDataId}`)
 * ```
 */
export function track(
  context: Context,
  action: Action,
  description: string,
  data?: EventPropsSchema
): Promise<void> {
  return getAnalytics().tracks(context, action, description, data)
}
